/**
 * Admin Users Management Page
 * View all users, manage credit balance, view transaction history
 */

import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Search, Edit2, Ban, Eye, Loader2, Trash2, ChevronLeft, ChevronRight, CheckSquare, Square } from 'lucide-react';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import AdminLayout from '@/components/AdminLayout';

interface User {
  id: number;
  name: string | null;
  email: string | null;
  phone: string | null;
  role: 'user' | 'admin';
  creditBalance: string;
  loginMethod: string | null;
  createdAt: Date;
  lastSignedIn: Date;
}

export default function AdminUsers() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'suspended' | 'banned'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'credit' | 'spent' | 'joined'>('joined');
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [creditAdjustment, setCreditAdjustment] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set());
  const itemsPerPage = 20;

  // Fetch users from API
  const { data: usersData, isLoading: isLoadingUsers, refetch } = trpc.admin.usersManagement.getUsers.useQuery(
    {
      page: currentPage,
      limit: itemsPerPage,
      search: searchQuery || undefined,
    }
  );

  const users = usersData?.users || [];
  const totalUsers = usersData?.total || 0;
  const totalPages = Math.ceil(totalUsers / itemsPerPage);

  // Adjust credit mutation
  const adjustCreditMutation = trpc.admin.usersManagement.adjustCreditBalance.useMutation({
    onSuccess: () => {
      toast.success('Credit adjusted successfully');
      setCreditAdjustment('');
      setAdjustmentReason('');
      setShowDetailModal(false);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to adjust credit');
    },
  });

  // Update role mutation
  const updateRoleMutation = trpc.admin.usersManagement.updateUserRole.useMutation({
    onSuccess: () => {
      toast.success('User role updated');
      setShowDetailModal(false);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update role');
    },
  });

  // Fetch user transactions for detail modal
  const { data: transactionsData, isLoading: isLoadingTransactions } = trpc.admin.usersManagement.getUserCreditTransactions.useQuery(
    { userId: selectedUser?.id || 0, limit: 10 },
    { enabled: !!selectedUser }
  );

  const userTransactions = transactionsData?.transactions || [];

  const handleRefresh = () => {
    refetch();
  };

  const filteredUsers = useMemo(() => {
    let result = users;

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'credit':
          return parseFloat(b.creditBalance) - parseFloat(a.creditBalance);
        case 'joined':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        default:
          return 0;
      }
    });

    return result;
  }, [users, sortBy]);

  const handleAdjustCredit = async () => {
    if (!selectedUser || !creditAdjustment || !adjustmentReason) {
      toast.error('Please fill in all fields');
      return;
    }

    const adjustment = parseInt(creditAdjustment);
    await adjustCreditMutation.mutateAsync({
      userId: selectedUser.id,
      amount: adjustment,
      reason: adjustmentReason,
    });
  };

  const suspendMutation = trpc.admin.usersManagement.suspendUser.useMutation({
    onSuccess: () => {
      toast.success('User suspended successfully');
      refetch();
      setShowDetailModal(false);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to suspend user');
    },
  });

  const unsuspendMutation = trpc.admin.usersManagement.unsuspendUser.useMutation({
    onSuccess: () => {
      toast.success('User unsuspended successfully');
      refetch();
      setShowDetailModal(false);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to unsuspend user');
    },
  });

  const banMutation = trpc.admin.usersManagement.banUser.useMutation({
    onSuccess: () => {
      toast.success('User banned successfully');
      refetch();
      setShowDetailModal(false);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to ban user');
    },
  });

  const handleSuspendUser = async (userId: number) => {
    const reason = prompt('Enter suspension reason:');
    if (!reason) return;
    await suspendMutation.mutateAsync({ userId, reason });
  };

  const handleUnsuspendUser = async (userId: number) => {
    if (!confirm('Unsuspend this user?')) return;
    await unsuspendMutation.mutateAsync({ userId });
  };

  const handleBanUser = async (userId: number) => {
    const reason = prompt('Enter ban reason:');
    if (!reason) return;
    await banMutation.mutateAsync({ userId, reason });
  };

  const handleBulkDelete = async () => {
    if (selectedUsers.size === 0) {
      toast.error('Please select users first');
      return;
    }
    if (!confirm(`Delete ${selectedUsers.size} users? This action cannot be undone.`)) return;
    toast.info('Bulk delete feature coming soon');
    setSelectedUsers(new Set());
  };

  const handleToggleSelectAll = () => {
    if (selectedUsers.size === filteredUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(filteredUsers.map(u => u.id)));
    }
  };

  const handleToggleUserSelect = (userId: number) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handlePromoteToAdmin = async (userId: number) => {
    if (!confirm('Promote this user to admin?')) return;
    await updateRoleMutation.mutateAsync({
      userId,
      newRole: 'admin',
    } as any);
  };

  const handleDemoteToUser = async (userId: number) => {
    if (!confirm('Demote this user to regular user?')) return;
    await updateRoleMutation.mutateAsync({
      userId,
      newRole: 'user',
    } as any);
  };

  const getStatusColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-black text-white border-black';
      case 'user':
        return 'bg-white text-black border-black';
      default:
        return 'bg-white text-black border-black';
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading font-bold text-3xl mb-1 text-black">Users Management</h1>
            <p className="text-black font-mono text-sm">Manage user accounts and credit balance</p>
          </div>
          <div className="text-right">
            <p className="font-heading font-bold text-2xl text-black">{users.length}</p>
            <p className="text-black font-mono text-xs">Total Users</p>
          </div>
        </div>

        {/* Filters */}
        <div className="backoffice-card border-3 p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative md:col-span-2">
              <Search size={16} className="absolute left-3 top-3 text-black/60" />
              <input
                type="text"
                placeholder="Search name/email/PlayX ID..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border-2 border-black font-mono text-sm focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value as any);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border-2 border-black font-mono text-sm focus:outline-none focus:ring-2 focus:ring-black"
            >
              <option value="joined">Sort by Joined</option>
              <option value="name">Sort by Name</option>
              <option value="credit">Sort by Credit</option>
              <option value="spent">Sort by Spent</option>
            </select>
          </div>
        </div>

        {/* Users Table */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="bg-white border-3 border-black overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-3 border-black bg-black text-white">
                  <th className="px-4 py-3 text-center font-heading font-bold text-sm w-10">
                    <button
                      onClick={handleToggleSelectAll}
                      className="hover:opacity-70 transition-opacity"
                    >
                      {selectedUsers.size === filteredUsers.length && filteredUsers.length > 0 ? (
                        <CheckSquare size={16} />
                      ) : (
                        <Square size={16} />
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left font-heading font-bold text-sm">Name</th>
                  <th className="px-4 py-3 text-left font-heading font-bold text-sm">PlayX ID</th>
                  <th className="px-4 py-3 text-left font-heading font-bold text-sm">Email</th>
                  <th className="px-4 py-3 text-right font-heading font-bold text-sm">Credit</th>
                  <th className="px-4 py-3 text-right font-heading font-bold text-sm">Spent</th>
                  <th className="px-4 py-3 text-center font-heading font-bold text-sm">Txn</th>
                  <th className="px-4 py-3 text-left font-heading font-bold text-sm">Status</th>
                  <th className="px-4 py-3 text-left font-heading font-bold text-sm">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingUsers ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-black/60">
                      <Loader2 className="inline h-5 w-5 animate-spin" />
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-black/60">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user, index) => (
                    <motion.tr
                      key={user.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className="border-b-2 border-black/10 hover:bg-black/5 transition-colors"
                    >
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleToggleUserSelect(user.id)}
                          className="hover:opacity-70 transition-opacity"
                        >
                          {selectedUsers.has(user.id) ? (
                            <CheckSquare size={16} />
                          ) : (
                            <Square size={16} />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3 font-heading font-bold text-sm">{user.name}</td>
                      <td className="px-4 py-3 font-mono text-sm text-black/60">ID-{user.id}</td>
                      <td className="px-4 py-3 font-mono text-sm text-black/60">{user.email}</td>
                      <td className="px-4 py-3 font-heading font-bold text-sm text-right">C{parseFloat(user.creditBalance).toLocaleString()}</td>
                      <td className="px-4 py-3 font-heading font-bold text-sm text-right">-</td>
                      <td className="px-4 py-3 font-heading font-bold text-sm text-center text-black/60">{userTransactions?.length || 0}</td>
                      <td className="px-4 py-3">
                        <span className={`px-3 py-1 font-heading font-bold text-xs border-2 inline-block ${getStatusColor(user.role)}`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setShowDetailModal(true);
                            }}
                            className="p-2 hover:bg-black/10 transition-colors"
                            title="View details"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setShowDetailModal(true);
                            }}
                            className="p-2 hover:bg-black/10 transition-colors"
                            title="Edit credit"
                          >
                            <Edit2 size={16} />
                          </button>
                          {user.role === 'user' && (
                            <button
                              onClick={() => handlePromoteToAdmin(user.id)}
                              disabled={adjustCreditMutation.isPending}
                              className="p-2 hover:bg-black/10 transition-colors disabled:opacity-50"
                              title="Promote to admin"
                            >
                              <Edit2 size={16} />
                            </button>
                          )}
                          {user.role === 'admin' && (
                            <button
                              onClick={() => handleDemoteToUser(user.id)}
                              disabled={updateRoleMutation.isPending}
                              className="p-2 hover:bg-black/10 transition-colors disabled:opacity-50"
                              title="Demote to user"
                            >
                              <Ban size={16} />
                            </button>
                          )}
                          <button
                            onClick={() => handleSuspendUser(user.id)}
                            disabled={suspendMutation.isPending}
                            className="p-2 hover:bg-red-100 transition-colors disabled:opacity-50"
                            title="Suspend user"
                          >
                            <Ban size={16} />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Bulk Actions */}
        {selectedUsers.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-black text-white p-4 flex items-center justify-between border-3 border-black"
          >
            <p className="font-heading font-bold">{selectedUsers.size} users selected</p>
            <div className="flex gap-2">
              <button
                onClick={handleBulkDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 font-heading font-bold text-sm transition-colors flex items-center gap-2"
              >
                <Trash2 size={16} />
                Delete Selected
              </button>
              <button
                onClick={() => setSelectedUsers(new Set())}
                className="px-4 py-2 border-2 border-white font-heading font-bold text-sm hover:bg-white/10 transition-colors"
              >
                Clear
              </button>
            </div>
          </motion.div>
        )}

        {/* Pagination */}
        <div className="flex items-center justify-between">
          <p className="font-mono text-sm text-black/60">
            Page {currentPage} of {totalPages} ({totalUsers} total users)
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border-2 border-black font-heading font-bold text-sm hover:bg-black/10 disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              <ChevronLeft size={16} />
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 border-2 border-black font-heading font-bold text-sm hover:bg-black/10 disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              Next
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* User Detail Modal */}
      {showDetailModal && selectedUser && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowDetailModal(false)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white border-3 border-black max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="p-6 border-b-3 border-black flex-shrink-0">
              <h2 className="font-heading font-bold text-xl">{selectedUser.name || 'User'}</h2>
              <p className="text-black/60 font-mono text-sm">ID-{selectedUser.id}</p>
            </div>

            {/* Tabs */}
            <div className="flex border-b-3 border-black">
              <button className="flex-1 px-4 py-3 border-r-3 border-black font-heading font-bold text-sm bg-black text-white">
                Details
              </button>
              <button className="flex-1 px-4 py-3 font-heading font-bold text-sm hover:bg-black/5 transition-colors">
                Transactions
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
              {/* User Info */}
              <div className="space-y-2 bg-black/5 p-3 border-2 border-black/10">
                <div className="flex justify-between text-sm">
                  <span className="text-black/60">Email:</span>
                  <span className="font-mono">{selectedUser.email}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-black/60">Joined:</span>
                  <span className="font-mono">{new Date(selectedUser.createdAt).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-black/60">Last Active:</span>
                  <span className="font-mono">{new Date(selectedUser.lastSignedIn).toLocaleDateString()}</span>
                </div>
              </div>

              {/* Recent Transactions */}
              {isLoadingTransactions ? (
                <div className="text-center py-4">
                  <Loader2 className="inline h-4 w-4 animate-spin" />
                </div>
              ) : userTransactions.length > 0 ? (
                <div className="space-y-2">
                  <p className="font-heading font-bold text-sm">Recent Transactions</p>
                  {userTransactions.slice(0, 5).map((txn: any) => (
                    <div key={txn.id} className="text-xs bg-black/5 p-2 border border-black/10 flex justify-between">
                      <span className="font-mono">{txn.type || 'Transaction'}</span>
                      <span className="font-heading font-bold">C{typeof txn.amount === 'number' ? txn.amount.toLocaleString() : txn.amount}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-black/60">No transactions yet</p>
              )}

              {/* Credit Adjustment */}
              <div className="space-y-3 border-t-2 border-black/10 pt-4">
                <p className="font-heading font-bold text-sm">Adjust Credit Balance</p>
                <div>
                  <label className="block text-xs font-mono text-black/60 mb-1">Current Balance</label>
                  <p className="font-heading font-bold text-lg">C{selectedUser.creditBalance.toLocaleString()}</p>
                </div>
                <div>
                  <label className="block text-xs font-mono text-black/60 mb-1">Amount (+ or -)</label>
                  <input
                    type="number"
                    value={creditAdjustment}
                    onChange={(e) => setCreditAdjustment(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-black font-mono text-sm focus:outline-none focus:ring-2 focus:ring-black"
                    placeholder="Enter amount"
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono text-black/60 mb-1">Reason</label>
                  <input
                    type="text"
                    value={adjustmentReason}
                    onChange={(e) => setAdjustmentReason(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-black font-mono text-sm focus:outline-none focus:ring-2 focus:ring-black"
                    placeholder="Reason for adjustment"
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t-3 border-black flex gap-3 flex-shrink-0">
              <button
                onClick={() => setShowDetailModal(false)}
                className="flex-1 px-4 py-2 border-2 border-black font-heading font-bold text-sm hover:bg-black/10 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAdjustCredit}
                disabled={loading || !creditAdjustment || !adjustmentReason}
                className="flex-1 px-4 py-2 bg-black text-white border-2 border-black font-heading font-bold text-sm hover:bg-black/90 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Adjusting...' : 'Adjust'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AdminLayout>
  );
}
